/**
 * Ownership-aware access to the onboarding wizard's sessionStorage working state.
 *
 * The wizard keeps its in-progress state in sessionStorage under global keys that carry no
 * reference to the organisation they belong to. Historically each exit path cleared a *different*
 * subset of those keys, so a form could start with company data blank but another organisation's
 * connection ids still present — which then got written onto the new organisation on submit.
 *
 * This module is the single source of truth for that key surface. It stamps the session with the
 * organisation it belongs to and discards everything when the active organisation changes, so a
 * new form always starts clean. Deliberately free of React and Supabase imports so it can be used
 * from contexts, plain utilities and Playwright `addInitScript`.
 */

/** Keys matched exactly. */
export const ONBOARDING_EXACT_KEYS = [
  'figabl_company_data',
  'figabl_connection_ids',
  'figabl_csv_connection_ids',
  'figabl_unsaved_connection_ids',
  'figabl_connection_save_epoch',
  'figabl_connection_id',
  'figabl_csv_connection_id',
  'figabl_submission_identifier',
  'connectionMap',
  'amazonAccountTypes',
  'selectedConnectorData',
  'selectedConnectorId',
  'selectedConnectionId',
  'connectorRedirect',
  'isReconnect',
  'savedFormStep',
  'draftId',
  'figabl_draft_token',
] as const;

/** Keys matched by prefix (per-connection scratch data). */
export const ONBOARDING_KEY_PREFIXES = ['connection_', 'csv_db_', 'connector_details_'] as const;

/**
 * MFA machinery is auth state, not form state — it must survive a clear, otherwise clearing
 * mid-flow would bounce a user back through verification.
 */
export const ONBOARDING_PRESERVED_PREFIXES = ['figabl_mfa_'] as const;

/** Identifies which organisation the current session state belongs to. */
export const ONBOARDING_OWNER_KEY = 'figabl_session_org_id';

/**
 * Sentinel for "a fresh anonymous form that has not yet been assigned an organisation id".
 * Distinct from `null`, which means "ownership unknown".
 */
export const ANONYMOUS_OWNER = 'new';

function isPreservedKey(key: string): boolean {
  return ONBOARDING_PRESERVED_PREFIXES.some((prefix) => key.startsWith(prefix));
}

/** True when `key` holds onboarding working state (the owner stamp itself is excluded). */
export function isOnboardingKey(key: string): boolean {
  if (isPreservedKey(key)) return false;
  if ((ONBOARDING_EXACT_KEYS as readonly string[]).includes(key)) return true;
  return ONBOARDING_KEY_PREFIXES.some((prefix) => key.startsWith(prefix));
}

/**
 * Remove every onboarding working-state key, including the owner stamp.
 * MFA keys are preserved unless `alsoClearMfa` is set.
 */
export function clearOnboardingSession(opts?: { alsoClearMfa?: boolean }): void {
  if (typeof sessionStorage === 'undefined') return;

  // Snapshot the keys before removing: mutating sessionStorage while walking it by index skips
  // entries. Object.keys works against both the browser Storage object and the test mock.
  const toRemove = Object.keys(sessionStorage).filter((key) => {
    if (isOnboardingKey(key) || key === ONBOARDING_OWNER_KEY) return true;
    return Boolean(opts?.alsoClearMfa) && isPreservedKey(key);
  });

  toRemove.forEach((key) => sessionStorage.removeItem(key));
}

export function getOnboardingSessionOwner(): string | null {
  if (typeof sessionStorage === 'undefined') return null;
  return sessionStorage.getItem(ONBOARDING_OWNER_KEY);
}

/** Stamp the session as belonging to `orgId` (or the anonymous sentinel when null). */
export function markOnboardingSessionOwner(orgId: string | null): void {
  if (typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(ONBOARDING_OWNER_KEY, orgId ?? ANONYMOUS_OWNER);
}

/**
 * Guarantee the session state belongs to `orgId`, wiping it first if it belongs to a different
 * organisation. Returns true when a clear occurred.
 *
 * An anonymous session acquiring its first real organisation id (`'new'` -> uuid) is an adoption,
 * not a mismatch: the state is kept and simply re-stamped. Clearing there would discard the
 * in-progress form of the very user who just filled it in.
 *
 * An unstamped session (`null`) is ownership-unknown legacy state and is never adopted: those
 * keys may be another organisation's connection ids from before this guard existed.
 */
export function ensureOnboardingSessionFor(orgId: string | null): boolean {
  if (typeof sessionStorage === 'undefined') return false;

  const next = orgId ?? ANONYMOUS_OWNER;
  const current = getOnboardingSessionOwner();

  if (current === next) return false;

  if (current === ANONYMOUS_OWNER && next !== ANONYMOUS_OWNER) {
    markOnboardingSessionOwner(next);
    return false;
  }

  clearOnboardingSession();
  markOnboardingSessionOwner(next);
  return true;
}

/**
 * True when this tab already holds an owned onboarding draft. After an
 * anonymous form is assigned an organisation id the owner stamp is no longer
 * `new`, so a later `/step/1` visit must use this — not "anonymous owner" —
 * to tell an in-progress draft from leftover foreign state.
 */
export function hasInProgressOnboardingDraft(): boolean {
  if (typeof sessionStorage === 'undefined') return false;
  if (!getOnboardingSessionOwner()) return false;
  return sessionStorage.getItem('figabl_company_data') !== null;
}

/**
 * Fresh `/step/1` with no continuation: drop leftovers, including unstamped
 * pre-upgrade ids. Those must not be stamped as `new` and later adopted.
 * A session already marked anonymous is left alone so an in-progress form survives refresh.
 * Returns true when a clear occurred.
 */
export function resetOnboardingForFreshAnonymousForm(): boolean {
  if (typeof sessionStorage === 'undefined') return false;
  if (getOnboardingSessionOwner() === ANONYMOUS_OWNER) return false;
  clearOnboardingSession();
  markOnboardingSessionOwner(null);
  return true;
}

function parseIdArray(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

/**
 * Connection ids recorded for `orgId`. Returns empty lists when the stored state belongs to a
 * different organisation — the fence that stops one organisation's connections being read into
 * another's form even if a clear was somehow missed.
 */
export function readOwnedConnectionIds(orgId: string | null): { standard: string[]; csv: string[] } {
  if (typeof sessionStorage === 'undefined') return { standard: [], csv: [] };

  const owner = getOnboardingSessionOwner();
  const expected = orgId ?? ANONYMOUS_OWNER;

  // An unstamped session predates this guard; treat its ids as unowned rather than trusting them.
  if (owner !== expected) return { standard: [], csv: [] };

  return {
    standard: parseIdArray(sessionStorage.getItem('figabl_connection_ids')),
    csv: parseIdArray(sessionStorage.getItem('figabl_csv_connection_ids')),
  };
}

/**
 * Organisation id for the form currently open in this tab.
 * Company data wins; the owner stamp covers the case where step 2 rewrote the
 * company blob without `organization_id` after the draft was saved.
 */
export function readOnboardingOrganisationId(): string | null {
  if (typeof sessionStorage === 'undefined') return null;

  const raw = sessionStorage.getItem('figabl_company_data');
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as { organization_id?: unknown; organisation_id?: unknown };
      if (typeof parsed.organization_id === 'string' && parsed.organization_id.length > 0) {
        return parsed.organization_id;
      }
      if (typeof parsed.organisation_id === 'string' && parsed.organisation_id.length > 0) {
        return parsed.organisation_id;
      }
    } catch {
      // Company data is unreadable; the owner stamp is the remaining source.
    }
  }

  const owner = getOnboardingSessionOwner();
  if (owner && owner !== ANONYMOUS_OWNER) return owner;
  return null;
}

/**
 * Company data this tab saved for `orgId`. Saves write it before React state updates,
 * so it can be newer than a component's `companyData` prop.
 */
export function readSessionCompanyData(orgId: string | null): { connections?: unknown } | null {
  if (typeof sessionStorage === 'undefined') return null;

  const raw = sessionStorage.getItem('figabl_company_data');
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { connections?: unknown; organization_id?: unknown; organisation_id?: unknown };
    const savedOrgId = parsed.organization_id || parsed.organisation_id || null;
    return savedOrgId === orgId ? parsed : null;
  } catch {
    return null;
  }
}

/** Persist connection ids for `orgId`, stamping ownership at the same time. */
export function writeOwnedConnectionIds(
  orgId: string | null,
  ids: { standard: string[]; csv: string[] },
): void {
  if (typeof sessionStorage === 'undefined') return;

  // A caller that could not resolve an id must not downgrade a real owner to the
  // anonymous sentinel. That stamp is what lets the EIN check exclude the organisation
  // being edited after a round trip to the connections step.
  const currentOwner = getOnboardingSessionOwner();
  const resolvedOrgId =
    orgId ?? (currentOwner && currentOwner !== ANONYMOUS_OWNER ? currentOwner : null);
  markOnboardingSessionOwner(resolvedOrgId);

  const standard = ids.standard.filter(Boolean);
  const csv = ids.csv.filter(Boolean);

  sessionStorage.setItem('figabl_connection_ids', JSON.stringify(standard));
  sessionStorage.setItem('figabl_csv_connection_ids', JSON.stringify(csv));

  if (standard.length > 0) {
    sessionStorage.setItem('figabl_connection_id', standard[0]);
  } else {
    sessionStorage.removeItem('figabl_connection_id');
  }

  // A removal writes the shorter list. Dropping the omitted ids here is what lets
  // a later empty reload stay empty instead of putting the connector back.
  dropUnsavedConnectionIdsExcept(new Set([...standard, ...csv]));
}

const UNSAVED_CONNECTION_IDS_KEY = 'figabl_unsaved_connection_ids';
const CONNECTION_SAVE_EPOCH_KEY = 'figabl_connection_save_epoch';

/**
 * `null` means this tab added the connector and no save has succeeded yet.
 * A number is the epoch of the save that succeeded.
 */
type UnsavedConnectionEpochs = Record<string, number | null>;

export type ConnectionSaveToken = {
  ids: string[];
};

function readUnsavedConnectionEpochs(): UnsavedConnectionEpochs {
  if (typeof sessionStorage === 'undefined') return {};
  const raw = sessionStorage.getItem(UNSAVED_CONNECTION_IDS_KEY);
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const epochs: UnsavedConnectionEpochs = {};
    for (const [id, value] of Object.entries(parsed)) {
      if (value === null || (typeof value === 'number' && Number.isFinite(value))) {
        epochs[id] = value;
      }
    }
    return epochs;
  } catch {
    return {};
  }
}

function writeUnsavedConnectionEpochs(epochs: UnsavedConnectionEpochs): void {
  if (typeof sessionStorage === 'undefined') return;
  if (Object.keys(epochs).length === 0) {
    sessionStorage.removeItem(UNSAVED_CONNECTION_IDS_KEY);
    return;
  }
  sessionStorage.setItem(UNSAVED_CONNECTION_IDS_KEY, JSON.stringify(epochs));
}

function dropUnsavedConnectionIdsExcept(keep: ReadonlySet<string>): void {
  const epochs = readUnsavedConnectionEpochs();
  let changed = false;
  for (const id of Object.keys(epochs)) {
    if (!keep.has(id)) {
      delete epochs[id];
      changed = true;
    }
  }
  if (changed) writeUnsavedConnectionEpochs(epochs);
}

/** Epoch of the latest connection save that succeeded in this tab. */
export function currentConnectionSaveEpoch(): number {
  if (typeof sessionStorage === 'undefined') return 0;
  const raw = sessionStorage.getItem(CONNECTION_SAVE_EPOCH_KEY);
  if (!raw) return 0;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

function bumpConnectionSaveEpoch(): number {
  const next = currentConnectionSaveEpoch() + 1;
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(CONNECTION_SAVE_EPOCH_KEY, String(next));
  }
  return next;
}

/**
 * Remember connectors this tab added that are not in the last persisted snapshot.
 * A draft reload that still has `connections: []` keeps these ids.
 */
export function noteUnsavedConnectionIds(ids: readonly string[]): void {
  const epochs = readUnsavedConnectionEpochs();
  let changed = false;
  for (const id of ids) {
    if (!id || Object.prototype.hasOwnProperty.call(epochs, id)) continue;
    epochs[id] = null;
    changed = true;
  }
  if (changed) writeUnsavedConnectionEpochs(epochs);
}

/**
 * Ids from `ids` that this tab added and has not successfully saved.
 * Pass the same list to `finishConnectionSave` when that save settles.
 */
export function beginConnectionSave(ids: readonly string[]): ConnectionSaveToken {
  const epochs = readUnsavedConnectionEpochs();
  return {
    ids: ids.filter((id) => Boolean(id) && epochs[id] === null),
  };
}

/** Record whether the save started by `beginConnectionSave` landed. */
export function finishConnectionSave(token: ConnectionSaveToken, success: boolean): void {
  if (!success) return;
  const epoch = bumpConnectionSaveEpoch();
  const epochs = readUnsavedConnectionEpochs();
  let changed = false;
  for (const id of token.ids) {
    if (epochs[id] === null) {
      epochs[id] = epoch;
      changed = true;
    }
  }
  if (changed) writeUnsavedConnectionEpochs(epochs);
}

/**
 * Session ids to keep when `persistedIds` does not include them.
 *
 * `fetchEpoch` is the save epoch captured when the snapshot request started.
 * A snapshot that started before a successful save is stale: it must not drop
 * the connector that save just wrote. A later snapshot that still omits a
 * confirmed id is a real removal.
 */
export function retainUnsavedConnectionIds(
  persistedIds: readonly string[],
  sessionIds: readonly string[],
  fetchEpoch: number,
): string[] {
  const epochs = readUnsavedConnectionEpochs();
  const persisted = new Set(persistedIds);
  const session = new Set(sessionIds);
  const retained: string[] = [];
  let changed = false;

  for (const id of Object.keys(epochs)) {
    if (persisted.has(id) || !session.has(id)) {
      delete epochs[id];
      changed = true;
      continue;
    }
    const confirmedAt = epochs[id];
    if (confirmedAt != null && fetchEpoch >= confirmedAt) {
      delete epochs[id];
      changed = true;
      continue;
    }
    retained.push(id);
  }

  if (changed) writeUnsavedConnectionEpochs(epochs);
  return retained;
}

/**
 * True when this tab has written *both* connection-id lists (including explicit
 * empty arrays). Distinguishes "never seeded" / a one-sided legacy leftover
 * from "user removed the last connector".
 *
 * A pre-upgrade session often has only `figabl_connection_ids`. Treating that
 * as complete would skip persisted CSV (or standard) connections on companyData
 * and submit the subset.
 */
export function sessionHasOwnedConnectionLists(): boolean {
  if (typeof sessionStorage === 'undefined') return false;
  return (
    sessionStorage.getItem('figabl_connection_ids') !== null &&
    sessionStorage.getItem('figabl_csv_connection_ids') !== null
  );
}

/**
 * Drop `idsToRemove` from the connection lists that belong to `orgId`. Matches
 * every identifier a connector can be stored under (Fivetran id vs generated
 * connectionId), then re-stamps ownership so a later form cannot read them back.
 *
 * Lists are read only when this session already belongs to `orgId` (or is an
 * anonymous draft being adopted by its first real id). A mismatched or
 * unstamped session is left untouched: writing its leftovers back would stamp
 * another organisation's remaining ids onto the form being edited.
 */
export function removeOwnedConnectionIds(orgId: string | null, idsToRemove: readonly string[]): void {
  if (typeof sessionStorage === 'undefined') return;

  const currentOwner = getOnboardingSessionOwner();
  const resolvedOrgId =
    orgId ?? (currentOwner && currentOwner !== ANONYMOUS_OWNER ? currentOwner : null);
  const expected = resolvedOrgId ?? ANONYMOUS_OWNER;
  const adoptingAnonymous =
    currentOwner === ANONYMOUS_OWNER && expected !== ANONYMOUS_OWNER;

  if (currentOwner !== expected && !adoptingAnonymous) return;

  const remove = new Set(idsToRemove.filter((id): id is string => Boolean(id)));
  const standard = parseIdArray(sessionStorage.getItem('figabl_connection_ids')).filter((id) => !remove.has(id));
  const csv = parseIdArray(sessionStorage.getItem('figabl_csv_connection_ids')).filter((id) => !remove.has(id));
  writeOwnedConnectionIds(resolvedOrgId, { standard, csv });
}
